<template>
  <tr
    class="job-row data-row"
    :class="{ 'is-openable': openable }"
    tabindex="0"
    :data-job="job.id"
    :title="openable ? 'Open the report' : null"
    @click="onRowActivate"
    @keydown.enter.self="onRowActivate">
    <td class="col-job">
      <div class="cell-main" :title="job.title">{{ job.title }}</div>
      <div class="cell-sub mono-id">{{ shortId }}</div>
    </td>
    <td>
      <span class="truncate" :title="scans.all">
        {{ scans.first }}
        <span v-if="scans.extra" class="cell-muted">+{{ scans.extra }}</span>
      </span>
    </td>
    <td><job-status-chip :job="job" /></td>
    <td class="num">
      <span v-if="job.findingsCount === null" class="cell-muted">{{ emDash }}</span>
      <strong v-else>{{ job.findingsCount }}</strong>
    </td>
    <td>
      <div>{{ startedAt }}</div>
      <div class="cell-sub">{{ startedAgo }}</div>
    </td>
    <td class="cell-muted">
      <span class="truncate" :title="author">{{ author }}</span>
    </td>
    <td class="actions">
      <span class="row-actions">
        <v-btn v-if="openable" text small color="primary" class="row-act" @click.stop="$emit('open', job)">
          <v-icon left :size="14">mdi-open-in-new</v-icon>
          Open
        </v-btn>
        <!-- Disabled buttons swallow hover; the wrapping span carries the tooltip. -->
        <v-tooltip v-else top>
          <template v-slot:activator="{ on, attrs }">
            <span class="row-act row-act--disabled" v-bind="attrs" v-on="on">
              <v-btn text small color="primary" disabled>Open</v-btn>
            </span>
          </template>
          <span>{{ openHint }}</span>
        </v-tooltip>
        <v-btn v-if="cancellable" text small color="error" class="row-act" :disabled="!!cancellationState" :loading="cancellationState === 'submitting'" @click.stop="$emit('cancel', job)">Cancel</v-btn>
        <!-- Rule 06: the kebab stays visible at low emphasis so touch and keyboard users reach every action. -->
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small class="row-more" :aria-label="`More actions for ${job.title}`" v-bind="attrs" v-on="on" @click.stop>
              <v-icon :size="18">mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="$emit('activity', job)">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-timeline-text-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Job activity</v-list-item-title>
            </v-list-item>
            <v-list-item :disabled="!openable" @click="$emit('open', job)">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-open-in-new</v-icon></v-list-item-icon>
              <v-list-item-title>Open report</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="cancellable" :disabled="!!cancellationState" @click="$emit('cancel', job)">
              <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-close-circle-outline</v-icon></v-list-item-icon>
              <v-list-item-title class="error--text">Cancel analysis</v-list-item-title>
            </v-list-item>
            <v-list-item @click="copyId">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-content-copy</v-icon></v-list-item-icon>
              <v-list-item-title>Copy job ID</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </span>
    </td>
  </tr>
</template>

<script>
import aiAnalysisPermissionMixin from '@/features/Dashboard/utils/aiAnalysisPermissionMixin';
  import { $formatDate } from '@/utils';
  import { warn } from '@/utils/debugLogger';
  import { JOB_UI_STATUS } from '../../utils/findingsMetrics';
  import { EM_DASH, formatRelative, scanSummary, shortJobId, userDisplayName } from '../../utils/jobsFormat';
  import JobStatusChip from './JobStatusChip.vue';

  const STARTED_FORMAT = 'DD MMM YYYY, HH:mm';
  const OPEN_HINT_RUNNING = 'The report opens when the analysis completes';
  const OPEN_HINT_INCOMPLETE = 'No report: the analysis did not complete';

  export default {
    mixins: [aiAnalysisPermissionMixin],
    name: 'JobRow',
    components: { JobStatusChip },
    props: {
      job: { type: Object, required: true },
      cancellationState: { type: String, default: null },
    },
    data() {
      return { emDash: EM_DASH };
    },
    computed: {
      openable() {
        return this.job.uiStatus === JOB_UI_STATUS.DONE;
      },
      cancellable() {
        return this.canRunAiAnalysis && this.job.uiStatus === JOB_UI_STATUS.RUNNING;
      },
      openHint() {
        return this.job.uiStatus === JOB_UI_STATUS.RUNNING ? OPEN_HINT_RUNNING : OPEN_HINT_INCOMPLETE;
      },
      shortId() {
        return shortJobId(this.job.id);
      },
      scans() {
        return scanSummary(this.job.scanNames);
      },
      startedAt() {
        return this.job.createDate ? $formatDate(this.job.createDate, STARTED_FORMAT) : EM_DASH;
      },
      startedAgo() {
        return formatRelative(this.job.createDate);
      },
      author() {
        return userDisplayName(this.job.createdBy);
      },
    },
    methods: {
      onRowActivate() {
        if (this.openable) this.$emit('open', this.job);
      },
      async copyId() {
        try {
          await navigator.clipboard.writeText(this.job.id);
          this.$dialog.notify.success('Job ID copied');
        } catch (err) {
          warn('[JobRow] clipboard unavailable', err);
          this.$dialog.notify.error('Could not copy the job ID');
        }
      },
    },
  };
</script>
